import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { Car } from '@/api/cars/types';
import {
  NO_VIN_TRIM_MESSAGE,
  selectedTrimId,
} from '@/api/partners/cars/use-car-detail-options';
import { ControlledInput, Input, Text } from '@/ui';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';

export const carSetupFormSchema = z.object({
  id: z.number(),
  trimId: z.string().min(1, 'Trim is required'),
  licensePlate: z.string().min(1, 'License plate is required'),
  licensePlateState: z.string().min(2, 'License plate state is required'),
  color: z.string().min(1, 'Color is required'),
  numSeats: z.string().optional(),
  gloveboxPin: z.string().min(4, 'Glovebox pin is required'),
});

export type CarSetupFormType = z.infer<typeof carSetupFormSchema>;

export default function CarSetupForm({
  trimOptions,
  trimLocked = false,
  licensePlateStateOptions,
  colorOptions,
  car,
  onChange,
  onValidChange,
  style,
  defaultValues,
}: {
  trimOptions: DropdownOption[];
  trimLocked?: boolean;
  licensePlateStateOptions: DropdownOption[];
  colorOptions: DropdownOption[];
  car: Car;
  onChange: (values: CarSetupFormType) => void;
  onValidChange: (isValid: boolean) => void;
  style?: any;
  defaultValues?: Partial<CarSetupFormType>;
}) {
  const { styles, theme } = useStyles(stylesheet);

  const {
    control,
    trigger,
    setValue,
    formState: { isValid, errors },
    getValues,
    reset,
    watch,
  } = useForm<CarSetupFormType>({
    defaultValues: {
      id: car.id,
      trimId: defaultValues?.trimId ?? car.trim?.id.toString() ?? undefined,
      licensePlate: defaultValues?.licensePlate ?? car.licensePlate ?? '',
      licensePlateState:
        defaultValues?.licensePlateState ??
        licensePlateStateOptions.find(
          (option) => option.value === car.licensePlateState
        )?.value ??
        '',
      color:
        defaultValues?.color ??
        colorOptions.find((option) => option.value === car.color)?.value ??
        '',
      numSeats: defaultValues?.numSeats ?? car.numSeats?.toString() ?? '5',
      gloveboxPin: defaultValues?.gloveboxPin ?? car.gloveboxPin ?? '',
    },
    resolver: zodResolver(carSetupFormSchema),
  });

  const [licensePlateState, trimId, color] = useWatch({
    control,
    name: ['licensePlateState', 'trimId', 'color'],
  });

  useEffect(() => {
    const nextTrimId = selectedTrimId({
      options: trimOptions,
      locked: trimLocked,
      currentTrimId: trimId,
    });

    if ((trimId ?? '') === nextTrimId) return;

    setValue('trimId', nextTrimId);
    trigger('trimId');
  }, [trimLocked, trimOptions, trimId, setValue, trigger]);

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid]);

  useEffect(() => {
    // Ensure parent state is initialized even when user makes no edits.
    onChange(getValues()); // initial sync

    const sub = watch(() => {
      onChange(getValues()); // subsequent sync
    });
    return () => sub.unsubscribe();
  }, [getValues, onChange, watch]);

  useEffect(() => {
    if (defaultValues) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={[styles.container, style]}>
      <View>
        <View style={styles.inputsContainer}>
          <Dropdown
            mode="single"
            label="Trim"
            options={trimOptions}
            disabled={trimLocked}
            placeHolderText="Select car's trim"
            onSelect={(selected: DropdownOption[]) => {
              setValue('trimId', selected[0].id);
              trigger();
            }}
            onClear={() => {
              setValue('trimId', '');
              trigger('trimId');
            }}
            values={trimId ? [trimId] : []}
            style={styles.input}
            error={
              trimOptions.length === 0
                ? NO_VIN_TRIM_MESSAGE
                : errors.trimId?.message
            }
          />

          <Input
            label="Year"
            value={car.vehicleYear.toString()}
            disabled
            containerStyle={styles.input}
          />

          <Dropdown
            mode="single"
            label="Exterior Color"
            options={colorOptions}
            placeHolderText="Select exterior color"
            onSelect={(selected: DropdownOption[]) => {
              setValue('color', selected[0].value);
              trigger('color');
            }}
            onClear={() => {
              setValue('color', '');
              trigger('color');
            }}
            values={color ? [color] : []}
            style={styles.input}
            error={errors.color?.message}
          />

          {car.brandName === 'Tesla' &&
          !['Model S', 'Model 3'].includes(car.modelName) ? (
            <ControlledInput
              control={control}
              name="numSeats"
              label="Seating capacity"
              keyboardType="number-pad"
              onBlur={() => {
                trigger('numSeats');
              }}
              onChangeText={(text) => {
                setValue('numSeats', text);
                trigger('numSeats');
              }}
              containerStyle={styles.input}
            />
          ) : null}
        </View>
      </View>

      <View>
        <Text type="bodyRegular">Registration</Text>
      </View>

      <View style={styles.inputsContainer}>
        <ControlledInput
          control={control}
          name="licensePlate"
          label="License Plate"
          onBlur={() => {
            trigger('licensePlate');
          }}
          onChangeText={(text) => {
            const upperText = text.toUpperCase();
            setValue('licensePlate', upperText);
            trigger('licensePlate');
          }}
          containerStyle={styles.input}
        />

        <Dropdown
          mode="single"
          label="License Plate State"
          options={licensePlateStateOptions}
          placeHolderText="Select license plate state"
          onSelect={(selected: DropdownOption[]) => {
            setValue('licensePlateState', selected[0].value);
            trigger();
          }}
          onClear={() => {
            setValue('licensePlateState', '');
            trigger('licensePlateState');
          }}
          values={licensePlateState ? [licensePlateState] : []}
          style={styles.input}
          error={errors.licensePlateState?.message}
        />

        <ControlledInput
          control={control}
          name="gloveboxPin"
          label="Glovebox Pin"
          onBlur={() => {
            trigger('gloveboxPin');
          }}
          onChangeText={(text) => {
            setValue('gloveboxPin', text);
            trigger('gloveboxPin');
          }}
          containerStyle={styles.input}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    gap: 16,
    maxWidth: {
      xs: '100%',
      md: 600,
    },
  },
  inputsContainer: {
    flex: 1,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    flexWrap: {
      md: 'wrap',
    },
    gap: 16,
  },
  input: {
    flexBasis: {
      md: '48%',
    },
    flexGrow: {
      md: 1,
    },
    minWidth: {
      md: 0,
    },
    maxWidth: {
      md: '50%',
    },
  },
}));
